import React, { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { FaSpinner } from 'react-icons/fa'; // Spinner icon

const SeatingChart = () => {
  const { t } = useTranslation();
  const { wedding, setWedding } = useAppContext();
  const [tables, setTables] = useState([]);
  const [guests, setGuests] = useState([]);
  const [tableName, setTableName] = useState('');
  const [assignTableId, setAssignTableId] = useState(null);
  const [loadingGuestId, setLoadingGuestId] = useState(null); // Track which checkbox is loading
  const [loadingAddTable, setLoadingAddTable] = useState(false); // Track Add Table loading
  const cardId = localStorage.getItem('id');

  const assignedGuestNames = tables.flatMap(table => (table.guests || []).map(guest => guest.guest_name));

  useEffect(() => {
    const fetchTablesAndGuests = async () => {
      if (!cardId) {
        console.error("Card ID not found in localStorage.");
        return;
      }
      try {
        const tablesResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/planner/tables?card_id=${cardId}`);
        setTables(Array.isArray(tablesResponse.data) ? tablesResponse.data : []);
      } catch (error) {
        console.error("Error fetching tables:", error);
      }
    };

    const fetchAttendingGuests = async () => {
      if (!cardId) {
        console.error("Card ID not found for guests.");
        return;
      }
      try {
        const guestsResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/planner/attending-guests?card_id=${cardId}`);
        setGuests(Array.isArray(guestsResponse.data) ? guestsResponse.data.map(guest => ({ name: guest.name })) : []);
      } catch (error) {
        console.error("Error fetching attending guests:", error);
      }
    };

    fetchTablesAndGuests();
    fetchAttendingGuests();
  }, [cardId, t]);

  const handleAddTable = async (e) => {
    e.preventDefault();
    if (tableName.trim() && cardId) {
      setLoadingAddTable(true);
      try {
        const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/planner/tables`, {
          card_id: cardId,
          table_name: tableName,
          table_number: (Array.isArray(tables) ? tables.length : 0) + 1,
        });
        setTables(prev => [...prev, response.data]);
        setTableName('');
      } catch (error) {
        console.error("Error adding table:", error);
      }
      setLoadingAddTable(false);
    }
  };

  const handleAssignGuest = async (tableId, guestName) => {
    if (!guestName || !cardId) return;
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/planner/table-guests`, {
        table_id: tableId,
        guest_name: guestName,
      });
      setTables(prevTables =>
        prevTables.map(table =>
          table.id === tableId
            ? { ...table, guests: [...(table.guests || []), response.data] }
            : table
        )
      );
    } catch (error) {
      console.error("Error assigning guest:", error);
    }
  };

  const handleRemoveGuest = async (tableId, guestId) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/planner/table-guests/${guestId}`);
      setTables(prevTables =>
        prevTables.map(table =>
          table.id === tableId
            ? { ...table, guests: (table.guests || []).filter(guest => guest.id !== guestId) }
            : table
        )
      );
    } catch (error) {
      console.error("Error removing guest:", error);
    }
  };

  const handleGuestCheckboxChange = async (tableId, guestName, isChecked) => {
    setLoadingGuestId(`${tableId}-${guestName}`);
    if (isChecked) {
      await handleAssignGuest(tableId, guestName);
    } else {
      const table = tables.find(t => t.id === tableId);
      const guestToRemove = (table.guests || []).find(g => g.guest_name === guestName);
      if (guestToRemove) {
        await handleRemoveGuest(tableId, guestToRemove.id);
      }
    }
    setLoadingGuestId(null);
  };

  const handleRemoveTable = async (id) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/planner/tables/${id}`);
      setTables(tables.filter(table => table.id !== id));
    } catch (error) {
      console.error("Error removing table:", error);
    }
  };

  const handleClearData = async () => {
    if (!cardId) {
      console.error("Card ID not found in localStorage. Cannot clear data.");
      return;
    }
    const confirmClear = window.confirm(t('seatingChart.confirmClearData'));
    if (confirmClear) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/planner/clear-seating-chart?card_id=${cardId}`);
        setTables([]);
        alert(t('seatingChart.dataClearedSuccess'));
      } catch (error) {
        console.error("Error clearing data:", error);
        alert(t('seatingChart.dataClearedError'));
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-10 px-2">
      <h1 className="text-3xl font-bold text-primary mb-2">{t('seatingChart.title')}</h1>
      <p className="text-foreground/70 mb-2">{t('seatingChart.description')}</p>
      <p className="text-sm text-foreground/50 mb-6 italic">
        {t('seatingChart.sampleDataNote')}
      </p>

      {/* Add Table + Clear All */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <form className="flex flex-col sm:flex-row gap-3 w-full md:w-auto" onSubmit={handleAddTable}>
          <input
            type="text"
            className="border rounded px-3 py-2 flex-1"
            placeholder={t('seatingChart.tableNamePlaceholder')}
            value={tableName}
            onChange={e => setTableName(e.target.value)}
            required
            disabled={loadingAddTable}
          />
          <button
            type="submit"
            className="btn-primary flex items-center justify-center"
            disabled={loadingAddTable}
          >
            {loadingAddTable && <FaSpinner className="animate-spin mr-2" />}
            {t('seatingChart.addTable')}
          </button>
        </form>
        <button
          onClick={handleClearData}
          className="bg-danger text-white px-4 py-3 rounded-lg hover:bg-red-600 transition-colors w-full md:w-auto"
          disabled={tables.length === 0}
        >
          {t('seatingChart.clearAllData')}
        </button>
      </div>

      {/* Tables */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {tables.length > 0 ? (
          tables.map((table) => (
            <div key={table.id} className="bg-white rounded shadow p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="font-bold text-lg text-primary">
                  {table.table_name} (Table {table.table_number})
                </span>
                <button onClick={() => handleRemoveTable(table.id)} className="text-danger px-2">
                  {t('seatingChart.removeTable')}
                </button>
              </div>

              {/* Assigned Guests */}
              <ul className="mb-3">
                {(table.guests || []).length === 0 && (
                  <li className="text-foreground/50">{t('seatingChart.noGuestsAssigned')}</li>
                )}
                {(table.guests || []).map((guest) => (
                  <li key={guest.id} className="flex justify-between items-center">
                    <span>{guest.guest_name}</span>
                    <button
                      onClick={() => handleRemoveGuest(table.id, guest.id)}
                      className="text-danger px-2"
                    >
                      {t('common.remove')}
                    </button>
                  </li>
                ))}
              </ul>

              {/* Assign Guests */}
              <div>
                <button
                  className={`${assignTableId === table.id ? 'bg-success' : 'bg-info'} text-white px-3 py-1 rounded hover:bg-secondary hover:text-gray-800 transition-colors mb-2`}
                  onClick={() => setAssignTableId(assignTableId === table.id ? null : table.id)}
                >
                  {assignTableId === table.id ? t('common.done') : t('seatingChart.assignGuest')}
                </button>

                {assignTableId === table.id && (
                  <div className="flex flex-col gap-2 mt-2 max-h-40 overflow-y-auto border rounded p-2">
                    {guests.filter(guest => {
                      const isAssignedToCurrentTable = (table.guests || []).some(tg => tg.guest_name === guest.name);
                      const isAssignedToAnotherTable = assignedGuestNames.includes(guest.name) && !isAssignedToCurrentTable;
                      return !isAssignedToAnotherTable;
                    }).map((guest, i) => {
                      const isAssignedToCurrentTable = (table.guests || []).some(tg => tg.guest_name === guest.name);
                      const isLoading = loadingGuestId === `${table.id}-${guest.name}`;
                      return (
                        <label key={i} className="flex items-center space-x-2">
                          {isLoading ? (
                            <FaSpinner className="animate-spin text-primary" />
                          ) : (
                            <input
                              type="checkbox"
                              className="form-checkbox"
                              checked={isAssignedToCurrentTable}
                              onChange={(e) => handleGuestCheckboxChange(table.id, guest.name, e.target.checked)}
                            />
                          )}
                          <span>{guest.name}</span>
                        </label>
                      );
                    })}
                    {guests.filter(guest => {
                      const isAssignedToCurrentTable = (table.guests || []).some(tg => tg.guest_name === guest.name);
                      const isAssignedToAnotherTable = assignedGuestNames.includes(guest.name) && !isAssignedToCurrentTable;
                      return !isAssignedToAnotherTable;
                    }).length === 0 && (
                      <span className="text-foreground/50">{t('seatingChart.noGuestsAvailable')}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="text-foreground/50 col-span-full">{t('seatingChart.noTablesYet')}</div>
        )}
      </div>
    </div>
  );
};

export default SeatingChart;
